import type { QueryClient } from '@tanstack/react-query';
import type { store as Store } from './store';
import { signedOut, userLoaded } from './slices/authSlice';
import { toastPushed } from './slices/uiSlice';
import { session } from '@/lib/api/session';
import { authService } from '@/lib/api/services/auth.service';
import {
  PLAN_LIMIT_EVENT,
  SESSION_EXPIRED_EVENT,
  type PlanLimitEventDetail,
} from '@/lib/api/client';

/**
 * Wires the API client's platform-wide events into app state.
 *
 * The client is deliberately framework-agnostic (it can't import the store
 * without a cycle), so it announces events and this module — the composition
 * root — decides what they mean.
 */
export function registerGlobalHandlers(store: typeof Store, queryClient: QueryClient): void {
  // Refresh failed: the session is unrecoverable. Drop identity and cached
  // tenant data so the login screen is clean.
  window.addEventListener(SESSION_EXPIRED_EVENT, () => {
    store.dispatch(signedOut());
    queryClient.clear();
  });

  // A plan limit hit anywhere — creating a lead, inviting a user, adding a key.
  window.addEventListener(PLAN_LIMIT_EVENT, (event) => {
    const { message, details } = (event as CustomEvent<PlanLimitEventDetail>).detail;
    store.dispatch(
      toastPushed(
        'Plan limit reached',
        message || `${details?.resource ?? 'Resource'} limit reached on the ${details?.tier} plan.`,
        'error',
      ),
    );
  });
}

/**
 * Refresh the signed-in identity from `/auth/me` once on load.
 *
 * The store hydrates from localStorage, which can be stale — most importantly
 * its permission set. That changes whenever an admin edits a role's grants or
 * the platform ships a module that adds permissions; without this, a returning
 * user keeps the old set until they manually log out and back in (and every
 * permission-gated screen shows "no access" in the meantime). Re-fetching /me
 * heals it on the next page load instead.
 *
 * Fire-and-forget: auth failures are handled by the client's 401 interceptor
 * (refresh, or SESSION_EXPIRED → signedOut), so there's nothing to do on error.
 */
export function bootstrapSession(store: typeof Store): void {
  if (!session.isAuthenticated) return;
  authService
    .me()
    .then((user) => {
      store.dispatch(userLoaded(user));
      session.save({ user }); // persist the refreshed identity for the next reload
    })
    .catch(() => {
      /* handled by the API client interceptor */
    });
}
